import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWheelbarrow } from '../context/WheelbarrowContext';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { WheelbarrowItem, Owner, Location } from '../types';
import { formatGBP } from '../utils/currency';
import StarRating from './StarRating';
import './Checkout.css';

const getOwner = (ownerId: string, data: Record<string, Owner>): Owner => {
  return data[ownerId] || { id: ownerId, name: 'Unknown Owner', rating: 0, totalRatings: 0 };
};

const formatAddress = (location: Location): string => {
  if (!location) return 'Address not available';
  const address = location.address || '';
  const city = location.city || '';
  const postcode = location.postcode || '';
  
  if (!address && !city && !postcode) return 'Address not available';
  
  return [address, city, postcode].filter(Boolean).join(', ');
};

const Checkout = () => {
  const { state } = useWheelbarrow();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getOwner } = useData();
  const [owners, setOwners] = useState<Record<string, Owner>>({});
  const [ownersLoading, setOwnersLoading] = useState(true);

  // Mock user profile data (this should come from an API/context)
  const mockUserProfile = {
    location: {
      address: "123 Garden Street",
      city: "Cambridge",
      postcode: "CB1 2AB",
      coordinates: {
        latitude: 52.2053,
        longitude: 0.1218
      }
    }
  };

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!user) {
      navigate('/login', { state: { from: '/checkout' } });
    }
  }, [user, navigate]);

  // Load owners for all items in wheelbarrow
  useEffect(() => {
    const loadOwners = async () => {
      try {
        setOwnersLoading(true);
        const ownerPromises = state.items.map(async (item) => {
          const owner = await getOwner(item.item.ownerId);
          return { ownerId: item.item.ownerId, owner };
        });
        
        const ownerResults = await Promise.all(ownerPromises);
        const ownersMap: Record<string, Owner> = {};
        
        ownerResults.forEach(({ ownerId, owner }) => {
          if (owner) {
            ownersMap[ownerId] = owner;
          } else {
            // Fallback to unknown owner if not found
            ownersMap[ownerId] = { id: ownerId, name: 'Unknown Owner', rating: 0, totalRatings: 0 };
          }
        });
        
        setOwners(ownersMap);
      } catch (error) {
        console.error('Error loading owners:', error);
      } finally {
        setOwnersLoading(false);
      }
    };

    if (state.items.length > 0) {
      loadOwners();
    }
  }, [state.items, getOwner]);

  // Don't render anything while checking authentication
  if (!user) return null;

  return (
    <div className="checkout-page">
      <div className="breadcrumb">
        <button onClick={() => navigate('/')} className="breadcrumb-item">
          Categories
        </button>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-item active">Checkout</span>
      </div>

      <div className="checkout-content">
        <h1>Checkout</h1>
        
        <div className="checkout-sections">
          <div className="checkout-main">
            <section className="rental-summary">
              <h2>Rental Summary</h2>
              <div className="rental-items">
                {state.items.map((item) => {
                  const owner = owners[item.item.ownerId] || { id: item.item.ownerId, name: 'Loading...', rating: 0, totalRatings: 0 };
                  return (
                    <div key={item.item.id} className="checkout-item">
                      <div className="item-details">
                        <h3>{item.item.name}</h3>
                        <div className="owner-details">
                          <p className="item-owner">From: {owner.name}</p>
                          <StarRating 
                            rating={owner.rating} 
                            totalRatings={owner.totalRatings}
                            size="small"
                          />
                        </div>
                        <p className="rental-period">
                          Duration: {item.days} day{item.days !== 1 ? 's' : ''}
                        </p>
                        <p className="daily-rate">
                          Rate: £{item.item.dailyRateGBP.toFixed(2)}/day
                        </p>
                      </div>
                      <div className="item-total">
                        £{(item.days * item.item.dailyRateGBP).toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="borrower-details">
              <h2>Your Details</h2>
              <div className="user-info">
                <img 
                  src={user.user_metadata?.picture || '/default-avatar.png'} 
                  alt={user.user_metadata?.name || 'User'} 
                  className="user-avatar"
                />
                <div className="user-details">
                  <p>{user.user_metadata?.name || user.email}</p>
                  <p className="user-email">{user.email}</p>
                </div>
              </div>
            </section>
          </div>

          <div className="checkout-sidebar">
            <div className="order-summary">
              <h2>Order Summary</h2>
              <div className="summary-details">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>{formatGBP(state.totalPence)}</span>
                </div>
                <div className="summary-row">
                  <span>Service Fee</span>
                  <span>{formatGBP(Math.round(state.totalPence * 0.1))}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>{formatGBP(Math.round(state.totalPence * 1.1))}</span>
                </div>
              </div>
              <button 
                className="confirm-rental"
                disabled={!mockUserProfile.location}
                onClick={() => {
                  if (!mockUserProfile.location) {
                    navigate('/profile');
                    return;
                  }
                  navigate('/payment-selection');
                }}
              >
                {mockUserProfile.location ? 'Proceed to Payment' : 'Add Address to Continue'}
              </button>
              <p className="terms">
                By continuing, you agree to our Terms of Service and Rental Agreement
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 